import { lazy, Suspense } from 'react';

import { api } from '../../api.js';
import { Loading } from '../../components/ui.jsx';

/**
 * Invoices in the web app: the client's invoices, quotes, transactions and add funds; the signed
 * pay and quote links from emails; staff pages for invoices, transactions and quotes, and their tabs
 * on the admin client page. Every page loads lazily (its own chunk).
 */
const page = (load, name) => () => load().then((m) => ({ Component: m[name] }));
const client = () => import('./client.jsx');
const pub = () => import('./public.jsx');
const admin = () => import('./admin.jsx');
const quotes = () => import('./admin-quotes.jsx');
const docs = () => import('./documents.jsx');

/** A lazily loaded component for tabs and widgets (routes use `lazy` instead). */
const later = (load, name) => {
  const C = lazy(() => load().then((m) => ({ default: m[name] })));
  return function Lazy(props) {
    return (
      <Suspense fallback={<Loading />}>
        <C {...props} />
      </Suspense>
    );
  };
};

export default {
  name: 'invoices',
  publicRoutes: [
    { path: 'pay/:uuid', lazy: page(pub, 'PublicPay') },
    { path: 'quote/:uuid', lazy: page(pub, 'PublicQuote') },
  ],
  clientRoutes: [
    { path: 'invoices', lazy: page(client, 'ClientInvoices') },
    { path: 'invoices/:uuid', lazy: page(client, 'ClientInvoice') },
    { path: 'quotes', lazy: page(client, 'ClientQuotes') },
    { path: 'quotes/:uuid', lazy: page(client, 'ClientQuote') },
    { path: 'account/add-funds', lazy: page(client, 'AddFunds') },
    { path: 'account/transactions', lazy: page(client, 'ClientTransactions') },
    { path: 'credit-notes', lazy: page(docs, 'ClientCreditNotes') },
  ],
  adminRoutes: [
    { path: 'invoices', lazy: page(admin, 'AdminInvoices') },
    { path: 'invoices/new', lazy: page(admin, 'AdminInvoiceNew') },
    { path: 'invoices/:uuid', lazy: page(admin, 'AdminInvoice') },
    { path: 'transactions', lazy: page(admin, 'AdminTransactions') },
    { path: 'quotes', lazy: page(quotes, 'AdminQuotes') },
    { path: 'quotes/:uuid', lazy: page(quotes, 'AdminQuote') },
    { path: 'credit-notes', lazy: page(docs, 'AdminCreditNotes') },
    { path: 'credit-notes/:uuid', lazy: page(docs, 'AdminCreditNote') },
    { path: 'disputes', lazy: page(docs, 'AdminDisputes') },
  ],
  clientNav: [
    {
      key: 'invoices',
      to: '/invoices',
      label: 'Invoices',
      order: 40,
      auth: true,
      // The sidebar's count: invoices still to pay.
      badge: {
        key: 'unpaid-invoices',
        label: 'nav.badge.invoices',
        load: async () => (await api('/api/client/invoices?status=open&page=1&per_page=1')).meta?.total ?? 0,
      },
    },
  ],
  adminNav: [
    { section: 'billing', to: '/admin/invoices', label: 'Invoices', permission: 'admin.invoices.read', order: 10 },
    {
      section: 'billing',
      to: '/admin/transactions',
      label: 'Transactions',
      permission: 'admin.transactions.read',
      order: 20,
    },
    { section: 'billing', to: '/admin/quotes', label: 'Quotes', permission: 'admin.quotes.read', order: 30 },
    {
      section: 'billing',
      to: '/admin/credit-notes',
      label: 'Credit notes',
      permission: 'admin.invoices.read',
      order: 35,
    },
    { section: 'billing', to: '/admin/disputes', label: 'Disputes', permission: 'admin.invoices.disputes', order: 36 },
  ],
  clientWidgets: [
    { key: 'unpaid-invoices', order: 20, Component: later(client, 'UnpaidInvoicesWidget') },
    { key: 'credit', order: 35, Component: later(client, 'CreditWidget') },
  ],
  adminClientTabs: [
    {
      key: 'invoices',
      label: 'Invoices',
      order: 30,
      permission: 'admin.invoices.read',
      Component: later(admin, 'InvoicesTab'),
    },
    {
      key: 'transactions',
      label: 'Transactions',
      order: 31,
      permission: 'admin.transactions.read',
      Component: later(admin, 'TransactionsTab'),
    },
    {
      key: 'quotes',
      label: 'Quotes',
      order: 32,
      permission: 'admin.quotes.read',
      Component: later(quotes, 'QuotesTab'),
    },
    {
      key: 'credit-notes',
      label: 'Credit notes',
      order: 35,
      // Same as Billing → Credit notes: reading them needs only the invoices permission.
      permission: 'admin.invoices.read',
      Component: later(docs, 'CreditNotesTab'),
    },
    {
      key: 'disputes',
      label: 'Disputes',
      order: 36,
      permission: 'admin.invoices.disputes',
      Component: later(docs, 'DisputesTab'),
    },
  ],
};
